import { z } from 'zod';

export * from './caps';

export const demoCopyStatusSchema = z.enum(['preparing', 'ready', 'failed', 'removed']);
export type DemoCopyStatus = z.infer<typeof demoCopyStatusSchema>;

/** The person's own copy, as their Settings page and the "Preparing your demo" screen read it. */
export const myDemoCopySchema = z.object({
  status: demoCopyStatusSchema,
  createdAt: z.number(),
  copiedAt: z.number(),
  questionsToday: z.number(),
  questionsPerDay: z.number(),
  fileReads: z.number(),
  fileReadsTotal: z.number(),
  /** While the copy is being made: how far it is, 0 to 1. */
  progress: z.number().optional(),
});
export type MyDemoCopy = z.infer<typeof myDemoCopySchema>;

/** One person's row on the template's Demo copies page. */
export const demoCopyRowSchema = z.object({
  copyId: z.string(),
  /** Null while the invite that built the copy is not accepted yet. */
  userId: z.string().nullable(),
  email: z.string(),
  status: demoCopyStatusSchema,
  createdAt: z.number(),
  lastActiveAt: z.number().nullable(),
  actions: z.number(),
  errors: z.number(),
  questionsToday: z.number(),
  fileReads: z.number(),
  /** The pending demo-viewer invite that built this copy, until it is accepted: the row offers Revoke. */
  invite: z.object({ inviteId: z.string() }).nullable().optional(),
});
export type DemoCopyRow = z.infer<typeof demoCopyRowSchema>;

export const demoCopyListSchema = z.object({
  rows: z.array(demoCopyRowSchema),
  /** Copies built ahead with nobody attached, handed to the next person so they do not wait. */
  spares: z.object({ ready: z.number(), preparing: z.number() }).optional(),
  questionsPerDay: z.number(),
  fileReadsTotal: z.number(),
  /** The signed-in manager's own copy, when they have one. */
  mine: z.object({ copyId: z.string().optional(), fundId: z.string(), status: demoCopyStatusSchema }).nullable(),
});
export type DemoCopyList = z.infer<typeof demoCopyListSchema>;

export const demoCopyEventSchema = z.object({
  at: z.number(),
  kind: z.enum(['write', 'error', 'question', 'file_read', 'capped', 'send_held', 'copy', 'view', 'feedback']),
  action: z.string(),
  status: z.number().nullable(),
  detail: z.string().nullable(),
});
export type DemoCopyEvent = z.infer<typeof demoCopyEventSchema>;
export const demoCopyActivitySchema = z.object({ email: z.string(), events: z.array(demoCopyEventSchema) });
export type DemoCopyActivity = z.infer<typeof demoCopyActivitySchema>;
